import { withRouter } from "react-router-dom";
import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { Query, Builder, Utils as QbUtils } from '@react-awesome-query-builder/ui';
import '../antd-less.css'; // need to enable LESS loader
import "@react-awesome-query-builder/ui/css/styles.css";
import "../react-awesome-query-builder.css";
import SessionContext from 'SessionContext';
import debounce from "lodash.debounce";

// For AntDesign widgets only:
import { AntdConfig } from '@react-awesome-query-builder/antd';
import CustomMultiSelect from '../core/ui/complex-filters/CustomMultiSelect';
import CustomAutocomplete from '../core/ui/complex-filters/CustomAutocomplete';

// Choose your skin (ant/material/vanilla):
// Create a local copy of the config so we don't mutate the frozen imported object
// We must deep clone settings to avoid cross-instance pollution
const InitialConfig = { 
  ...AntdConfig, 
  settings: {
    ...AntdConfig.settings,
    valueSourcesInfo: {
      value: { ...AntdConfig.settings.valueSourcesInfo.value },
    }
  },
  widgets: {
    ...AntdConfig.widgets,
    multiselect: {
      ...AntdConfig.widgets.multiselect,
      factory: (props) => {
        if (props.useAsyncSearch || props.showSearch) {
          return <CustomAutocomplete {...props} multiple={true} />;
        }
        return <CustomMultiSelect {...props} />;
      },
    },
  }
};
InitialConfig.settings.addRuleLabel = "Add filter";

// You can load query value from your backend storage (for saving see `Query.onChange()`)
const defaultQueryValue = { "id": QbUtils.uuid(), "type": "group" };

const apiSearchEndpoints = {
  _county: "location/get-filtered-counties-map",
  _congressional: "location/get-filtered-cd-map",
  _cbsa: "location/get-filtered-cbsa-map",
  _zip: "location/get-filtered-zipcodes-list"
};

const ComplexFilterBuilder = (props) => {
  const context = useContext(SessionContext);

  // Ref so asyncFetch closures always call the latest fetchFunction
  const fetchRef = useRef(null);

  async function fetchFunction(args, type) {
    const search = args[0] || "";
    if (!search) return;
    const apiUrl = apiSearchEndpoints[type];
    const res = await context.query(apiUrl, { query: search });
    const list = Object.entries(res).map((item) => {
      if (!Array.isArray(res)) {
        return { value: item[0], title: item[1] };
      } else {
        return { value: item[1], title: item[1] };
      }
    });
    return { values: list };
  }
  fetchRef.current = fetchFunction;

  function updateAsyncFetchFunction(fields) {
    if (!fields) return fields;
    const newFields = {};
    Object.keys(fields).forEach(key => {
      const field = fields[key];
      const newField = field.fieldSettings
        ? { ...field, fieldSettings: { ...field.fieldSettings } }
        : { ...field, fieldSettings: {} };
      const fs = newField.fieldSettings;
      if (fs && key.endsWith('_county_fips')) {
        fs['asyncFetch'] = (...args) => fetchRef.current(args, '_county');
        fs.useAsyncSearch = true;
        newField.showSearch = true;
        fs.allowCustomValues = false;
      } else if (fs && key.endsWith('_congress')) {
        fs['asyncFetch'] = (...args) => fetchRef.current(args, '_congressional');
        fs.useAsyncSearch = true;
        fs.allowCustomValues = false;
      } else if (fs && key.endsWith('_cbsa')) {
        fs['asyncFetch'] = (...args) => fetchRef.current(args, '_cbsa');
        fs.useAsyncSearch = true;
        fs.allowCustomValues = false;
      } else if (key.endsWith('_zip') || key.endsWith('zip')) {
        if (fs) fs.allowCustomValues = true;
        newField.allowCustomValues = true;
      }
      // Clean up titles for uploaded files
      if (key.includes("_up_") && fs && fs.listValues) {
        const normalizeTitle = (val) => {
          const parts = ((val && val.title) || "").split('|');
          return { ...val, title: parts[0] || '' };
        };
        if (Array.isArray(fs.listValues)) {
          fs.listValues = fs.listValues.map(normalizeTitle);
        } else if (typeof fs.listValues === 'object' && fs.listValues !== null) {
          fs.listValues = Object.keys(fs.listValues).reduce((acc, listValueKey) => {
            acc[listValueKey] = normalizeTitle(fs.listValues[listValueKey]);
            return acc;
          }, {});
        }
      }
      
      // Inject the explicit uiType from the backend into the field config so our CustomMultiSelect knows which Modal to show
      if (field.gui && field.gui.uiType) {
        if (!newField.fieldSettings) newField.fieldSettings = {};
        newField.fieldSettings.uiType = field.gui.uiType;
      }
      
      const isCustomRange = field.gui && (field.gui.uiType === 'birthdate-range' || field.gui.uiType === 'date-range' || field.gui.uiType === 'year-range' || field.gui.uiType === 'int-range' || field.gui.uiType === 'float-range' || field.gui.uiType === 'age-range');
      
      if (isCustomRange) {
        newField.type = "multiselect";
        newField.valueSources = ['value'];
        if (!newField.fieldSettings) newField.fieldSettings = {};
        
        // CRITICAL: Ensure listValues is present so RAQB treats it as a valid multiselect,
        // but put a fake trigger value inside so that the empty-list-stripper doesn't delete it!
        newField.fieldSettings.listValues = [ { value: "trigger", title: "trigger" } ];
        newField.fieldSettings.allowCustomValues = true;
        
        newField.operators = ['multiselect_equals', 'multiselect_not_equals'];
        newField.defaultOperator = 'multiselect_equals';
        delete newField.widgets;
      }
      
      // Fix for fields that should be dates/numbers/ranges but are being forced into selects
      if (fs && fs.listValues && !isCustomRange) {
        if (fs.listValues.length === 0 || ['date', 'datetime', 'time', 'number', 'slider', 'rangeslider', 'year'].includes(newField.type)) {
          delete fs.listValues;
        }
      }
      
      // In RAQB v6, date fields without valueSources explicitly set will sometimes fallback to text or select.
      // We must explicitly declare the allowed widgets and sources for these types.
      if (['date', 'datetime', 'time'].includes(newField.type)) {
        newField.valueSources = ['value'];
      }
      
      // Speedeon uses a custom React modal for DOB and Year Ranges built into the MultiSelect widget.
      // We must ensure that these fields are typed as 'multiselect' so they trigger our CustomMultiSelect.jsx component.
      const isDOB = key.includes("ameps__v-epsln-demog-000033") || key.includes("bday_range") || key.includes("birthdate_range") || (newField.label && newField.label.toLowerCase().includes("date of birth"));
      const isYearRange = key.includes("ameps__v-epsln-demog-000031") || key.includes("ameps__v-epsln-demog-000030") || key.includes("year_range") || key.includes("age_range");

      if (isDOB || isYearRange) {
        newField.type = "multiselect";
        newField.valueSources = ['value'];
        // In RAQB v6, field config must have a `widgets` property for widget resolution.
        // Without it, getWidgetForFieldOp returns null and nothing renders.
        newField.widgets = {
          multiselect: {
            operators: ['multiselect_equals', 'multiselect_not_equals'],
          }
        };
        newField.mainWidget = 'multiselect';
        if (newField.fieldSettings) {
          newField.fieldSettings.listValues = [
            { value: "trigger", title: "trigger" } // Ensure it's not stripped by our previous listValues.length === 0 check
          ];
          newField.fieldSettings.allowCustomValues = true;
        }
        newField.operators = ['multiselect_equals', 'multiselect_not_equals'];
      }

      // Fix multiselect values.join crash by ensuring list values are treated as arrays
      if (['multiselect', 'treemultiselect'].includes(newField.type)) {
         newField.valueSources = ['value'];
      }
      
      newFields[key] = newField;
    });
    return newFields;
  }

  const [state, setState] = useState(() => {
    const queryValue = props.initialQuery ? { ...props.initialQuery } : { ...defaultQueryValue };
    if (!('id' in queryValue)) {
      queryValue.id = QbUtils.uuid();
    }

    const config = {
      ...InitialConfig,
      settings: { ...InitialConfig.settings },
      fields: updateAsyncFetchFunction(props.fields),
    };
    
    if (props.readonly) {
      config.settings.immutableOpsMode = true;
      config.settings.immutableValuesMode = true;
      config.settings.immutableGroupsMode = true;
      config.settings.immutableFieldsMode = true;
      config.settings.showNot = false;
    } else {
      config.settings.immutableOpsMode = false;
      config.settings.immutableValuesMode = false;
      config.settings.immutableGroupsMode = false;
      config.settings.immutableFieldsMode = false;
      config.settings.showNot = false; // set to true or false to show/hide the "not" option
    }
    config.settings.extras = context.command;
    config.settings.renderSize = "medium";

    // The RAQB v6 `checkTree` validation expects multiselect values to strictly be arrays.
    // If old saved queries have a string instead of an array, it crashes calling `.join()`
    // Also normalize the operator for DOB/year-range fields that were saved with the old operator name.
    const multiselectFieldKeys = ['ameps__v-epsln-demog-000033', 'ameps__v-epsln-demog-000031', 'ameps__v-epsln-demog-000030'];
    const fixMultiselectValues = (query) => {
      if (!query || typeof query !== 'object') return query;
      if (query.type === 'rule' && query.properties) {
        const fieldKey = query.properties.field;
        const isMultiselectField = fieldKey && multiselectFieldKeys.some(k => fieldKey.includes(k));

        // Fix operator: old saved rules may have "equal" instead of "multiselect_equals"
        if (isMultiselectField && query.properties.operator && !query.properties.operator.includes('multiselect')) {
          query.properties.operator = query.properties.operator === 'not_equal'
            ? 'multiselect_not_equals'
            : 'multiselect_equals';
        }

        if (query.properties.value) {
          const isMultiselect = query.properties.operator && query.properties.operator.includes('multiselect');
          let val = query.properties.value;

          if (isMultiselect) {
            if (!Array.isArray(val)) {
              query.properties.value = [[val]];
            } else if (val.length > 0 && !Array.isArray(val[0])) {
              query.properties.value = [val];
            }
          } else {
            // For non-multiselect, ensure value is a flat array [val]
            if (Array.isArray(val) && val.length > 0 && Array.isArray(val[0])) {
              query.properties.value = val[0];
            }
          }
        }
      }
      if (query.children1) {
        Object.keys(query.children1).forEach(key => {
          query.children1[key] = fixMultiselectValues(query.children1[key]);
        });
      }
      return query;
    };
    
    const safeQueryValue = fixMultiselectValues(JSON.parse(JSON.stringify(queryValue)));
    let initialTree = QbUtils.loadTree(safeQueryValue);
    
    return {
      tree: QbUtils.checkTree(initialTree, config),
      config,
    };
  });

  const updateQueryRef = useRef(props.updateQuery);
  useEffect(() => {
    updateQueryRef.current = props.updateQuery;
  }, [props.updateQuery]);

  const debouncedUpdateQuery = useMemo(
    () =>
      debounce((jsonTree, logicFormat) => {
        updateQueryRef.current && updateQueryRef.current(jsonTree, logicFormat);
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedUpdateQuery.flush();
      debouncedUpdateQuery.cancel();
    };
  }, [debouncedUpdateQuery]);

  useEffect(() => {
    const jsonTree = QbUtils.getTree(state.tree, true, false);
    // Wrap jsonLogicFormat in a try/catch. The formatOp crash originates inside QbUtils.jsonLogicFormat 
    // when it tries to format the SQL/JsonLogic output string for a multi-select operator.
    let logicFormat = null;
    try {
      logicFormat = QbUtils.jsonLogicFormat(state.tree, state.config);
    } catch (e) {
      console.error("Failed to generate jsonLogicFormat:", e);
    }
    props.modified && props.updateQuery && props.updateQuery(jsonTree, logicFormat);
  // eslint-disable-next-line
  }, []);

  function onChange(immutableTree, config) {
    setState({ tree: immutableTree, config });
    const jsonTree = QbUtils.getTree(immutableTree, true, false);
    let logicFormat = null;
    try {
      logicFormat = QbUtils.jsonLogicFormat(immutableTree, config);
    } catch (e) {
      console.error("Failed to generate jsonLogicFormat in onChange:", e);
    }
    debouncedUpdateQuery(jsonTree, logicFormat);
  }

  function renderBuilder(builderProps) {
    return (
      <div className={"query-builder-container" + (props.readonly ? ' read-only' : '')} style={{ padding: '10px' }}>
        <div className="query-builder qb-lite">
          <Builder {...builderProps} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Query
        {...state.config}
        value={state.tree}
        onChange={onChange}
        renderBuilder={renderBuilder}
      />
      {/*renderResult(state)*/}
    </div>
  );
};

export default withRouter(ComplexFilterBuilder);
